// file: frontend/src/slices/logSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import LogDataService from "../services/LogService";
import { errorLog } from "../utils/debugUtils";
import { getRejectedMessage, toRejectedPayload } from "../utils/sliceErrorUtils";

// --- Initial State ---
const initialState = {
	items: [],
	meta: {},
	status: {
		// Keep separate status buckets so GET /log and POST /log/search can evolve
		// independently without confusing shared loading/error state.
		fetchRecent: "idle",
		search: "idle",
		create: "idle",
	},
	error: {
		fetchRecent: null,
		search: null,
		create: null,
	},
};

// --- Async Thunks ---
export const fetchRecentLogs = createAsyncThunk("logs/fetchRecent", async (params, { rejectWithValue }) => {
	try {
		const res = await LogDataService.getRecent(params);
		return res.data;
	} catch (err) {
		errorLog(3, "[logSlice][FLOW] fetchRecentLogs failed", { error: err, params });
		return rejectWithValue(toRejectedPayload(err, "Failed to fetch logs"));
	}
});

export const searchLogsThunk = createAsyncThunk("logs/search", async (params, { rejectWithValue }) => {
	try {
		// User Activity uses the body-based search endpoint so richer filter payloads
		// do not have to live in the URL.
		const res = await LogDataService.search(params);
		return res.data;
	} catch (err) {
		errorLog(3, "[logSlice][FLOW] searchLogsThunk failed", { error: err, params });
		return rejectWithValue(toRejectedPayload(err, "Failed to fetch logs"));
	}
});

export const createLog = createAsyncThunk("logs/create", async ({ log }, { rejectWithValue }) => {
	try {
		const res = await LogDataService.create({ log });
		return res.data;
	} catch (err) {
		errorLog(3, "[logSlice][FLOW] createLog failed", { error: err });
		return rejectWithValue(toRejectedPayload(err, "Failed to create log"));
	}
});

// --- Slice ---
const logSlice = createSlice({
	name: "log",
	initialState,
	reducers: {
		clearLogs(state) {
			state.items = [];
			state.meta = {};
		},
	},
	extraReducers: (builder) => {
		// fetchRecentLogs
		builder
			.addCase(fetchRecentLogs.pending, (state) => {
				state.status.fetchRecent = "loading";
				state.error.fetchRecent = null;
			})
			.addCase(fetchRecentLogs.fulfilled, (state, action) => {
				state.status.fetchRecent = "succeeded";
				state.items = action.payload.items || [];
				state.meta = action.payload.meta || {};
			})
			.addCase(fetchRecentLogs.rejected, (state, action) => {
				state.status.fetchRecent = "failed";
				state.error.fetchRecent = getRejectedMessage(action);
			});

		// searchLogsThunk
		builder
			.addCase(searchLogsThunk.pending, (state) => {
				state.status.search = "loading";
				state.error.search = null;
			})
			.addCase(searchLogsThunk.fulfilled, (state, action) => {
				state.status.search = "succeeded";
				state.items = action.payload.items || [];
				state.meta = action.payload.meta || {};
			})
			.addCase(searchLogsThunk.rejected, (state, action) => {
				state.status.search = "failed";
				state.error.search = getRejectedMessage(action);
			});

		// createLog
		builder
			.addCase(createLog.pending, (state) => {
				state.status.create = "loading";
				state.error.create = null;
			})
			.addCase(createLog.fulfilled, (state, action) => {
				state.status.create = "succeeded";
				state.items.unshift(action.payload);
			})
			.addCase(createLog.rejected, (state, action) => {
				state.status.create = "failed";
				state.error.create = getRejectedMessage(action);
			});
	},
});

export const { clearLogs } = logSlice.actions;
export default logSlice.reducer;

// import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
// import LogDataService from "../services/LogService";

// const initialState = [];

// export const createLog = createAsyncThunk(
// 	"logs/create",
// 	async ({ log }) => {
// 		// console.log('createAsyncThunk');
// 		const res = await LogDataService.create({ log });
// 		return res.data;
// 	}
// );

// const logSlice = createSlice({
// 	name: "log",
// 	initialState,
// 	// extraReducers: {
// 	// 	[createLog.fulfilled]: (state, action) => {
// 	// 		state.push(action.payload);
// 	// 	},
// 	// },
// });

// const { reducer } = logSlice;
// export default reducer;
